import { FeeDefinition, FieldType } from '@pmp/api';
import { CrudMode, FormItem, SelectsOptions, transformUppercaseNoSpaces } from '@ohpen/enso-ui';
import type { RenderFormItem } from '@ohpen/enso-ui';
import { t } from 'i18next';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, HALF_WIDTH, crudSection, textLimitProps, withColumnDecimals } from '@pmp/utils';
import { renderReadonlyWithUnit } from '@pmp/components';

import { feesColumns } from './tableConfig';

const feesFormSchema = (
  selectsOptions: SelectsOptions,
  renderFeeBands: RenderFormItem<{ index: number }>,
  renderAlert: RenderFormItem<{ index: number }>,
  mode?: CrudMode | null,
  isUsed?: boolean | null,
): FormItem[] => {
  const disabled = mode === CrudMode.VIEW || isUsed;

  return [
    {
      renderFormItem: renderAlert,
    },
    crudSection('global.labels.general', mode, [
      {
        title: 'pmp.fees.labels.feeId',
        dataIndex: 'feeId',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
          getValueFromEvent: transformUppercaseNoSpaces,
        },
        fieldProps: {
          disabled: mode !== CrudMode.CREATE,
          ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
        },
      },
      {
        title: 'pmp.fees.labels.feeName',
        dataIndex: 'feeName',
        colProps: HALF_WIDTH,
        tooltip: t('pmp.fees.labels.feeNameHint'),
        formItemProps: {
          required: true,
        },
        fieldProps: {
          disabled: mode === CrudMode.VIEW,
          ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
        },
      },
      {
        title: 'pmp.fees.labels.description',
        dataIndex: 'description',
        tooltip: t('pmp.fees.labels.descriptionHint'),
        formItemProps: {
          required: true,
        },
        fieldProps: {
          disabled: mode === CrudMode.VIEW,
          ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
        },
      },
    ]),
    crudSection('pmp.fees.labels.calculationSection', mode, [
      {
        title: 'pmp.fees.labels.feeType',
        dataIndex: 'feeType',
        valueType: 'select',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
        },
        fieldProps: {
          options: selectsOptions[FieldType.FEE_TYPE],
          disabled,
        },
      },
      {
        title: 'pmp.fees.labels.calculationType',
        dataIndex: 'calculationType',
        valueType: 'select',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
        },
        fieldProps: {
          options: selectsOptions[FieldType.CALCULATION_TYPE],
          disabled,
        },
      },
      {
        valueType: 'dependency',
        dependencies: ['calculationType'],
        columns: [
          {
            title: t('pmp.fees.labels.amount'),
            dataIndex: 'amount',
            colProps: HALF_WIDTH,
            valueType: 'money',
            fieldProps: { disabled, addonBefore: '£' },
            renderReadonly: renderReadonlyWithUnit({ prefix: '£' }),
            formItemProps: form => {
              const calculationType = form.getFieldValue('calculationType');
              const isFixed = calculationType === 'FIXEDAMOUNT' || calculationType?.key === 'FIXEDAMOUNT';
              return { required: isFixed, hidden: !isFixed };
            },
          },
          {
            title: t('pmp.fees.labels.percentage'),
            dataIndex: 'percentage',
            colProps: HALF_WIDTH,
            valueType: 'percent',
            fieldProps: { disabled },
            renderReadonly: renderReadonlyWithUnit({ suffix: '%' }),
            formItemProps: form => {
              const calculationType = form.getFieldValue('calculationType');
              const isPercentage = calculationType === 'PERCENTAGE' || calculationType?.key === 'PERCENTAGE';
              return { required: isPercentage, hidden: !isPercentage };
            },
          },
          {
            title: t('pmp.fees.labels.feeBands'),
            dataIndex: 'bands',
            renderFormItem: renderFeeBands,
            fieldProps: { disabled },
            formItemProps: form => {
              const calculationType = form.getFieldValue('calculationType');
              const isBanded = calculationType === 'BANDEDFIXEDAMOUNT' || calculationType?.key === 'BANDEDFIXEDAMOUNT';
              return { required: isBanded, hidden: !isBanded };
            },
          },
          {
            title: t('pmp.fees.labels.basedOn'),
            dataIndex: 'basedOn',
            colProps: HALF_WIDTH,
            valueType: 'select',
            fieldProps: { options: selectsOptions[FieldType.PRODUCT_FEE_BASED_ON], disabled },
            formItemProps: form => {
              const calculationType = form.getFieldValue('calculationType');
              const isPercentage = calculationType === 'PERCENTAGE' || calculationType?.key === 'PERCENTAGE';
              return { required: isPercentage, hidden: !isPercentage };
            },
          },
        ],
      },
    ]),
    crudSection('pmp.fees.labels.paymentSection', mode, [
      {
        title: 'pmp.fees.labels.paymentMethods',
        dataIndex: 'paymentMethods',
        colProps: HALF_WIDTH,
        valueType: 'select',
        readonlyDisplay: 'tag',
        formItemProps: {
          required: true,
        },
        fieldProps: {
          mode: 'multiple',
          tagColor: 'blue',
          options: selectsOptions[FieldType.PAYMENT_METHOD],
          disabled,
        },
      },
      {
        title: 'pmp.fees.labels.paymentStages',
        dataIndex: 'paymentStages',
        colProps: HALF_WIDTH,
        valueType: 'select',
        readonlyDisplay: 'tag',
        formItemProps: {
          required: true,
        },
        fieldProps: {
          mode: 'multiple',
          tagColor: 'blue',
          options: selectsOptions[FieldType.PAYMENT_STAGE],
          disabled,
        },
      },
    ]),
    crudSection('pmp.fees.labels.accountingSection', mode, [
      {
        title: 'pmp.fees.labels.accountCode',
        dataIndex: 'accountCode',
        colProps: HALF_WIDTH,
        fieldProps: {
          disabled,
          ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
        },
      },
      {
        title: 'pmp.fees.labels.costCentre',
        dataIndex: 'costCentre',
        colProps: HALF_WIDTH,
        fieldProps: {
          disabled,
          ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
        },
      },
    ]),
    crudSection('pmp.fees.labels.conditionsSection', mode, [
      {
        title: 'pmp.fees.labels.refundable',
        dataIndex: 'refundable',
        valueType: 'select',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
        },
        fieldProps: {
          options: selectsOptions[FieldType.YES_NO],
          disabled,
        },
      },
      {
        title: 'pmp.fees.labels.waivable',
        dataIndex: 'waivable',
        valueType: 'select',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
        },
        fieldProps: {
          options: selectsOptions[FieldType.YES_NO],
          disabled,
        },
      },
      {
        title: 'pmp.fees.labels.estimated',
        dataIndex: 'estimated',
        valueType: 'select',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
        },
        fieldProps: {
          options: selectsOptions[FieldType.YES_NO],
          disabled,
        },
      },
    ]),
  ];
};

export const getFeesFormSchema = (...args: Parameters<typeof feesFormSchema>): FormItem[] =>
  withColumnDecimals(feesFormSchema(...args), feesColumns);

export const getFeeProfilesFormSchema = (
  selectsOptions: SelectsOptions,
  renderAlert: RenderFormItem<FeeDefinition>,
  mode?: CrudMode | null,
  isUsed?: boolean | null,
): FormItem[] => {
  const disabled = mode === CrudMode.VIEW || isUsed;

  return [
    {
      renderFormItem: renderAlert,
    },
    crudSection('global.labels.general', mode, [
      {
        title: 'pmp.fees.labels.feeProfileId',
        dataIndex: 'feeProfileId',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
          getValueFromEvent: transformUppercaseNoSpaces,
        },
        fieldProps: {
          disabled: mode !== CrudMode.CREATE,
          ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
        },
      },
      {
        title: 'pmp.fees.labels.feeProfileName',
        dataIndex: 'feeProfileName',
        colProps: HALF_WIDTH,
        formItemProps: {
          required: true,
        },
        fieldProps: {
          disabled: mode === CrudMode.VIEW,
          ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
        },
      },
    ]),
    crudSection('pmp.fees.labels.feesSection', mode, [
      {
        title: 'pmp.fees.labels.feeDefinitions',
        dataIndex: 'feeIds',
        valueType: 'select',
        readonlyDisplay: 'tag',
        fieldProps: {
          mode: 'multiple',
          tagColor: 'blue',
          optionFilterProp: 'label',
          showSearch: true,
          options: selectsOptions[FieldType.FEES],
          disabled,
        },
      },
    ]),
  ];
};
